import React from 'react';
import { Sheet } from './ui/sheet';
import { Button } from './ui/button';
import { PhoneOff, PowerOff, AlertCircle } from 'lucide-react';

interface LeaveMeetingSheetProps {
    open: boolean;
    onClose: () => void;
    isHost: boolean;
    onLeave: () => void;
    onEnd: () => void;
    isEndingMeeting?: boolean;
    meetingType?: 'instant' | 'scheduled';
}

const LeaveMeetingSheet: React.FC<LeaveMeetingSheetProps> = ({
    open,
    onClose,
    isHost,
    onLeave,
    onEnd,
    isEndingMeeting = false,
    meetingType = 'instant',
}) => {
    const isScheduledMeeting = meetingType === 'scheduled';
    const showEndMeetingOption = isHost && !isScheduledMeeting;
    const handleLeave = () => {
        onClose();
        onLeave();
    };

    const handleEnd = () => {
        onClose();
        onEnd();
    };

    return (
        <Sheet open={open} onClose={onClose} title="Leave Meeting">
            <div className="p-6 space-y-4">
                {showEndMeetingOption ? (
                    <>
                        {/* Host View for Instant Meetings - Two Options */}
                        <div className="space-y-4">
                            {/* Description */}
                            <p className="text-sm text-muted-foreground">
                                You can leave the meeting while letting others continue, or end it for everyone.
                            </p>

                            {/* Action Buttons */}
                            <div className="space-y-3">
                                {/* Leave Meeting Button */}
                                <Button
                                    onClick={handleLeave}
                                    variant="outline"
                                    size="lg"
                                    className="w-full justify-start h-auto py-4 px-4"
                                >
                                    <PhoneOff className="h-5 w-5 mr-3 flex-shrink-0 text-orange-500" />
                                    <div className="text-left flex-1">
                                        <div className="font-semibold">Leave Meeting</div>
                                        <div className="text-xs text-muted-foreground font-normal mt-0.5">
                                            Exit but allow others to continue
                                        </div>
                                    </div>
                                </Button>

                                {/* End Meeting Button */}
                                <Button
                                    onClick={handleEnd}
                                    disabled={isEndingMeeting}
                                    variant="destructive"
                                    size="lg"
                                    className="w-full justify-start h-auto py-4 px-4"
                                >
                                    {isEndingMeeting ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-3" />
                                    ) : (
                                        <PowerOff className="h-5 w-5 mr-3 flex-shrink-0" />
                                    )}
                                    <div className="text-left flex-1">
                                        <div className="font-semibold">End Meeting for All</div>
                                        <div className="text-xs font-normal mt-0.5 opacity-90">
                                            Terminate and close the meeting permanently
                                        </div>
                                    </div>
                                </Button>
                            </div>

                            {/* Warning Message */}
                            <div className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                    Ending the meeting will disconnect all participants and cannot be undone.
                                </p>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Regular Participant View - Single Option */}
                        <div className="space-y-4">
                            {/* Description */}
                            <p className="text-sm text-muted-foreground">
                                You can leave this meeting and rejoin anytime using the same link.
                            </p>

                            {/* Leave Button */}
                            <Button
                                onClick={handleLeave}
                                variant="outline"
                                size="lg"
                                className="w-full justify-start h-auto py-4 px-4"
                            >
                                <PhoneOff className="h-5 w-5 mr-3 flex-shrink-0 text-orange-500" />
                                <div className="text-left flex-1">
                                    <div className="font-semibold">Leave Meeting</div>
                                    <div className="text-xs text-muted-foreground font-normal mt-0.5">
                                        Exit while others continue
                                    </div>
                                </div>
                            </Button>

                            {/* Info Message */}
                            <div className="flex items-start gap-3 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                                <AlertCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-primary">
                                    The meeting will continue for other participants after you leave.
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Sheet>
    );
};

export default LeaveMeetingSheet;
